import React from "react";
import { Card } from "@/components/ui/card";
import { fmtDate } from "@/lib/format";

const BUILTIN = { ministeriale: ["Ministeriale", "#5B7FE2"], visita: ["Visita", "#7C3AED"], uscita: ["Uscita", "#E2725B"], permesso: ["Permesso", "#F59E0B"], altro: ["Altro", "#556B2F"] };
const PALETTE = ["#0D9488", "#DB2777", "#B45309", "#1C352D", "#2563EB", "#9333EA", "#65A30D", "#DC2626"];
export const kindLabel = (k) => BUILTIN[k]?.[0] || `${k.charAt(0).toUpperCase()}${k.slice(1)}`;
export const kindColor = (k, i = 0) => BUILTIN[k]?.[1] || PALETTE[i % PALETTE.length];
const MONTHS = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];

function Kpi({ label, value, hint, testId }) {
  return (
    <Card className="p-4 border border-stone-200 rounded-xl shadow-none" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-widest text-stone-500">{label}</div>
      <div className="text-2xl font-semibold mt-1 text-[#1C352D]">{value}</div>
      {hint && <div className="text-xs text-stone-500 mt-0.5">{hint}</div>}
    </Card>
  );
}

export function ImpegniKpis({ s }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
      <Kpi label="Impegni" value={s.impegni} hint="giornate di impegno nel periodo" testId="imp-kpi-impegni"/>
      <Kpi label="Partecipazioni" value={s.presenze} hint="studenti × impegni" testId="imp-kpi-presenze"/>
      <Kpi label="Studenti coinvolti" value={`${s.involved}/${s.students}`} hint="almeno un impegno" testId="imp-kpi-involved"/>
      <Kpi label="Mai coinvolti" value={s.never} hint="nessun impegno nel periodo" testId="imp-kpi-never"/>
      <Kpi label="Media · Massimo" value={`${s.avg} · ${s.max}`} hint="impegni per studente" testId="imp-kpi-avg"/>
    </div>
  );
}

export function KindTotals({ kinds, colorOf }) {
  if (!kinds.length) return null;
  const max = Math.max(...kinds.map((k) => k.presenze), 1);
  return (
    <Card className="p-4 border border-stone-200 shadow-none rounded-xl" data-testid="imp-kinds">
      <div className="space-y-2">
        {kinds.map((k) => (
          <div key={k.kind} className="grid grid-cols-[110px_1fr] sm:grid-cols-[160px_1fr_260px] items-center gap-x-3 gap-y-1 text-sm" data-testid={`imp-kind-${k.kind}`}>
            <span className="inline-flex items-center gap-2 truncate"><span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: colorOf(k.kind) }}/>{kindLabel(k.kind)}</span>
            <div className="h-4 bg-stone-100 rounded overflow-hidden"><div className="h-full rounded" style={{ width: `${(k.presenze / max) * 100}%`, background: colorOf(k.kind) }}/></div>
            <span className="text-xs text-stone-600 font-mono text-right"><b>{k.impegni}</b> impegni · <b>{k.presenze}</b> partecip. · <b>{k.students}</b> stud.</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

export function MonthlyTrend({ months, kinds, colorOf }) {
  const max = Math.max(...months.map((m) => m.total), 1);
  return (
    <Card className="p-4 border border-stone-200 shadow-none rounded-xl overflow-x-auto" data-testid="imp-trend">
      <div className="flex items-end gap-3 h-44 min-w-fit">
        {months.map((m) => (
          <div key={m.month} className="flex flex-col items-center gap-1 w-12 shrink-0" data-testid={`imp-month-${m.month}`}>
            <span className="text-[11px] font-mono text-stone-600">{m.total || ""}</span>
            <div className="w-8 flex flex-col-reverse rounded overflow-hidden bg-stone-100" style={{ height: `${Math.max(4, (m.total / max) * 128)}px` }}>
              {kinds.map((k) => m.by_kind[k.kind] ? <div key={k.kind} title={`${kindLabel(k.kind)}: ${m.by_kind[k.kind]}`} style={{ height: `${(m.by_kind[k.kind] / m.total) * 100}%`, background: colorOf(k.kind) }}/> : null)}
            </div>
            <span className="text-[11px] text-stone-500">{MONTHS[Number(m.month.slice(5, 7)) - 1]} {m.month.slice(2, 4)}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

export function StudentRanking({ students, kinds, colorOf }) {
  const max = Math.max(1, ...students.map((r) => r.total));
  return (
    <Card className="p-4 border border-stone-200 shadow-none rounded-xl" data-testid="imp-ranking">
      <div className="space-y-1.5">
        {students.map((r) => (
          <div key={r.student_id} className="grid grid-cols-[140px_1fr_60px] sm:grid-cols-[220px_1fr_200px] items-center gap-3 text-sm" data-testid={`imp-rank-${r.student_id}`}>
            <div className="truncate" title={r.name}>{r.name}<span className="text-[11px] text-stone-400 ml-1">{r.year_level ? `${r.year_level}°` : ""}</span></div>
            <div className="h-5 bg-stone-100 rounded flex overflow-hidden" style={{ width: `${Math.max(2, (r.total / max) * 100)}%` }}>
              {kinds.map((k) => r.by_kind[k.kind] ? <div key={k.kind} title={`${kindLabel(k.kind)}: ${r.by_kind[k.kind]}`} style={{ width: `${(r.by_kind[k.kind] / r.total) * 100}%`, background: colorOf(k.kind) }}/> : null)}
            </div>
            <div className="text-right text-xs">
              <b className="font-mono">{r.total}</b>
              {r.total ? <span className="hidden sm:inline text-stone-500 ml-2" title={r.last_title || ""}>ultimo {fmtDate(r.last_date)}</span>
                : <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">Mai</span>}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
